import { View, Text, TouchableOpacity, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { legibleOn } from '../../lib/contrast'
import { tierColor, ROMAN } from '../../lib/badges'
import BadgeMedal from './BadgeMedal'

// Nome del badge nella lingua dell'app, con il livello in cifre romane quando la famiglia ne ha piu' di uno
export function useBadgeText() {
  const { t } = useTranslation()
  return {
    familyTitle: (f) => t(`badges.fam.${f.category}.title`),
    levelTitle: (f, l) => f.maxLevel > 1 ? `${t(`badges.fam.${f.category}.title`)} ${ROMAN[l.level - 1] ?? l.level}` : t(`badges.fam.${f.category}.title`),
    levelDesc: (f, l) => t(`badges.fam.${f.category}.desc`, { n: l.threshold }),
    tierName: (f, l) => f.maxLevel > 1 ? t(`badges.tier_${Math.min(l.level, 4)}`) : t('badges.tier_special'),
  }
}

// Scheda di una famiglia di badge nella griglia dell'album
export default function BadgeCard({ family, onPress }) {
  const tc = useThemeColors()
  const { t } = useTranslation()
  const txt = useBadgeText()
  const hidden = family.secret && !family.earned            // badge segreto non ancora ottenuto
  const shown = family.top ?? family.next                    // livello da mostrare: il piu' alto ottenuto, altrimenti il primo
  const color = tierColor(family.top?.level ?? 1, family.maxLevel)
  const state = hidden ? 'secret' : family.earned ? 'earned' : 'locked'
  const showBar = !hidden && !family.yearly && family.next && family.value > 0
  const title = hidden ? t('badges.secret_title') : (family.earned ? txt.levelTitle(family, family.top) : txt.levelTitle(family, shown))
  const status = hidden
    ? t('badges.secret_hint')
    : family.earned
      ? (family.yearly && family.seasons > 1 ? t('badges.seasons', { count: family.seasons }) : (family.complete ? t('badges.complete') : txt.tierName(family, family.top)))
      : t('badges.locked')

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.75}
      accessibilityRole="button"
      accessibilityLabel={`${title}. ${status}${showBar ? `. ${t('badges.progress', { value: family.value, threshold: family.next.threshold })}` : ''}`}
      style={[s.card, { backgroundColor: family.earned ? color + '14' : tc.bgCard2, borderColor: family.earned ? color + '66' : tc.border }]}
    >
      <BadgeMedal icon={family.icon} color={color} size={46} state={state} />
      <Text style={[s.title, { color: hidden ? tc.textMuted : tc.text }]} numberOfLines={2}>{title}</Text>
      <Text style={[s.status, { color: family.earned ? legibleOn(color, tc.bgCard, 3) : tc.textMuted }]} numberOfLines={2}>{status}</Text>
      {showBar && (
        <View style={s.barWrap}>
          <View style={[s.barBg, { backgroundColor: tc.border }]}>
            <View style={[s.barFill, { backgroundColor: color, width: `${Math.round(family.ratio * 100)}%` }]} />
          </View>
          <Text style={[s.barText, { color: tc.textMuted }]}>{family.value} / {family.next.threshold}</Text>
        </View>
      )}
    </TouchableOpacity>
  )
}

const s = StyleSheet.create({
  card: { width: '48.5%', minHeight: 150, borderWidth: 1, borderRadius: 14, padding: 12, gap: 6 },
  title: { fontFamily: fonts.condensedBold, fontSize: 15, lineHeight: 18 },
  status: { fontSize: 12, lineHeight: 16 },
  barWrap: { marginTop: 'auto', gap: 4 },
  barBg: { height: 5, borderRadius: 3, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 3 },
  barText: { fontSize: 12 },
})
